import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { Download } from 'lucide-react';
import { TimeIntervalFilter } from '@/components/TimeIntervalFilter';
import { useReportDateRange } from '@/hooks/useReportDateRange';
import { businessReportsApi, type BillWiseCategory } from '@/api/businessReports';
import { downloadBlob } from '@/lib/downloadBlob';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';

export default function CaBillWiseReports() {
  const [category, setCategory] = useState<BillWiseCategory>('All');
  const [exporting, setExporting] = useState(false);
  const {
    fromIso,
    toIso,
    selectedInterval,
    setSelectedInterval,
    selectedMonth,
    setSelectedMonth,
    selectedYear,
    setSelectedYear,
    handleTimeIntervalChange,
  } = useReportDateRange('this_month');

  const { data: report, isLoading } = useQuery({
    queryKey: ['ca-bill-wise', fromIso, toIso, category],
    queryFn: () =>
      businessReportsApi.billWiseReport({
        from: fromIso!,
        to: toIso!,
        category,
      }),
    enabled: !!fromIso && !!toIso,
  });

  const dateSuffix =
    fromIso && toIso
      ? `${format(new Date(fromIso), 'yyyy-MM-dd')}-to-${format(new Date(toIso), 'yyyy-MM-dd')}`
      : 'report';

  const runExport = async () => {
    if (!fromIso || !toIso) return;
    setExporting(true);
    try {
      const resp = await businessReportsApi.billWiseReportExport({
        from: fromIso,
        to: toIso,
        category,
      });
      const cat = category === 'All' ? 'all' : category.replace(/\s+/g, '-').toLowerCase();
      downloadBlob(
        new Blob([resp.data], { type: 'text/csv;charset=utf-8' }),
        `Bill-wise-report-${cat}-${dateSuffix}.csv`
      );
      toast.success('Download started');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const totals = report?.totals;

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-white">Bill-wise report</h2>
        <p className="text-slate-400">
          All hospital bills in one list — OPD, Lab, Pharma, Additional Services, Day Care, IPD, ICU, and IPD pharma billed separately.
        </p>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/5 p-4 space-y-4">
        <TimeIntervalFilter
          onTimeIntervalChange={handleTimeIntervalChange}
          selectedInterval={selectedInterval}
          onIntervalChange={setSelectedInterval}
          selectedMonth={selectedMonth}
          onMonthChange={setSelectedMonth}
          selectedYear={selectedYear}
          onYearChange={setSelectedYear}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Select value={category} onValueChange={(v) => setCategory(v as BillWiseCategory)}>
            <SelectTrigger className="w-[200px] bg-white/5 border-white/10 text-white">
              <SelectValue placeholder="Category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="All">All categories</SelectItem>
              <SelectItem value="OPD">OPD</SelectItem>
              <SelectItem value="Lab">Lab</SelectItem>
              <SelectItem value="Pharma">Pharma</SelectItem>
              <SelectItem value="Day Care">Day Care</SelectItem>
              <SelectItem value="Additional Services">Additional Services</SelectItem>
              <SelectItem value="IPD">IPD</SelectItem>
              <SelectItem value="ICU">ICU</SelectItem>
              <SelectItem value="IPD Pharma">IPD Pharma (billed separately)</SelectItem>
              <SelectItem value="Mixed">Mixed</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            className="border-white/15 bg-white/5 text-white hover:bg-white/10"
            disabled={!fromIso || !toIso || exporting}
            onClick={runExport}
          >
            <Download className="mr-2 h-4 w-4" />
            {exporting ? 'Exporting…' : 'Download CSV'}
          </Button>
        </div>
      </div>

      {totals && (
        <div className="grid grid-cols-3 gap-3">
          {[
            { label: 'Bills', value: totals.count, format: 'integer' as const },
            { label: 'Net amount', value: totals.netAmount, format: 'currency' as const },
            { label: 'Paid amount', value: totals.paidAmount, format: 'currency' as const },
          ].map((t) => (
            <div key={t.label} className="rounded-xl border border-white/10 bg-white/5 p-4">
              <p className="text-xs text-slate-400">{t.label}</p>
              <p className="mt-1 text-xl font-semibold tabular-nums text-white">
                {t.format === 'currency'
                  ? `₹${t.value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                  : t.value.toLocaleString('en-IN')}
              </p>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-2xl border border-white/10 bg-white/5 overflow-hidden">
        {isLoading ? (
          <p className="p-4 text-slate-400">Loading…</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10 hover:bg-transparent">
                  <TableHead className="text-slate-400">Category</TableHead>
                  <TableHead className="text-slate-400">Bill no.</TableHead>
                  <TableHead className="text-slate-400">Date</TableHead>
                  <TableHead className="text-slate-400">Patient</TableHead>
                  <TableHead className="text-slate-400">UHID</TableHead>
                  <TableHead className="text-right text-slate-400">Net</TableHead>
                  <TableHead className="text-right text-slate-400">Paid</TableHead>
                  <TableHead className="text-right text-slate-400">Due</TableHead>
                  <TableHead className="text-slate-400">Status</TableHead>
                  <TableHead className="text-slate-400">Payment</TableHead>
                  <TableHead className="text-slate-400">IPD no.</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(report?.bills ?? []).map((row) => (
                  <TableRow key={`${row.billCategory}-${row.billId}`} className="border-white/10">
                    <TableCell className="text-white whitespace-nowrap">{row.billCategory}</TableCell>
                    <TableCell className="font-medium text-white whitespace-nowrap">{row.billNumber}</TableCell>
                    <TableCell className="text-slate-300 whitespace-nowrap">
                      {format(new Date(row.billDate), 'dd/MM/yyyy')}
                    </TableCell>
                    <TableCell className="text-slate-300">{row.patientName}</TableCell>
                    <TableCell className="text-slate-300 whitespace-nowrap">{row.uhid || '—'}</TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{row.netAmount.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{row.paidAmount.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-right tabular-nums text-slate-200">
                      ₹{row.dueAmount.toLocaleString('en-IN')}
                    </TableCell>
                    <TableCell className="text-slate-300 whitespace-nowrap">{row.status}</TableCell>
                    <TableCell className="text-slate-400 whitespace-nowrap">{row.paymentModes || '—'}</TableCell>
                    <TableCell className="text-slate-400 whitespace-nowrap">{row.ipdNumber || '—'}</TableCell>
                  </TableRow>
                ))}
                {(!report?.bills || report.bills.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={11} className="text-center text-slate-500 py-8">
                      No bills for this period.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </div>
  );
}
